<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="450px"
  >
    <v-card>
      <!-- Logo header -->
      <div
        class="pa-4"
        justify="center"
        align="center"
        color="secondary"
        style="background-color:#666;"
      >
        <img
          src="@/assets/images/main-logo.png"
          alt="WhaleWisdom logo"
          style="max-height: 50px"
        >
        <v-btn
          dark
          icon
          absolute
          class="ma-1"
          style="top: 0; right: 0"
          @click="dialog = false"
        >
          <v-icon medium>
            close
          </v-icon>
        </v-btn>
      </div>

      <!-- Tabs: Log In / Sign Up -->
      <v-tabs
        v-model="currentTab"
        grow
      >
        <v-tab href="#log-in-tab">
          Log In
        </v-tab>
        <v-tab href="#sign-up-tab">
          Sign Up
        </v-tab>
      </v-tabs>
      <v-tabs-items
        v-model="currentTab"
        touchless
        class="py-1"
      >
        <!-- Log In -->
        <v-tab-item value="log-in-tab">
          <v-card-text>
            <v-form
              ref="logInForm"
              v-model="validLogIn"
              lazy-validation
              @submit.prevent="logIn()"
            >
              <v-text-field
                ref="email"
                v-model.trim="logInFields.email"
                :rules="[rules.required]"
                prepend-icon="email"
                label="E-mail/Username*"
                @keyup.enter="logIn()"
              />
              <v-text-field
                v-model.trim="logInFields.password"
                :rules="[rules.required]"
                :type="logInFields.showPassword ? 'text' : 'password'"
                :append-icon="logInFields.showPassword ? 'visibility_off' : 'visibility'"
                label="Password*"
                prepend-icon="lock"
                @click:append="logInFields.showPassword = !logInFields.showPassword"
                @keyup.enter="logIn()"
              />
              <v-checkbox
                v-model="logInFields.rememberMe"
                label="Remember me"
              />
            </v-form>
            <v-alert
              :value="Boolean(logInFields.error)"
              type="error"
              class="mt-4"
            >
              {{ logInFields.error }}
            </v-alert>
          </v-card-text>
        </v-tab-item>

        <!-- Sign Up -->
        <v-tab-item value="sign-up-tab">
          <v-card-text v-show="signUpCompleted">
            <helpers-section-sub-header class="mb-6">
              Thanks for signing up!
            </helpers-section-sub-header>
            <p>Click the verification link in the e-mail we sent, to validate your account before logging in.</p>
          </v-card-text>

          <v-card-text v-show="!signUpCompleted">
            <div class="mb-6">
              <p class="font-weight-medium">
                Creating an account on WhaleWisdom is free and easy.
              </p>
              <p>While much of WhaleWisdom doesn't require an account, creating one makes using the site easier...</p>
              <ul>
                <li>Save and edit your e-mail alerts</li>
                <li>Gain access to the WhaleWisdom API</li>
                <li>Create groups of filers to run backtests against and save your favorite backtests</li>
                <li>
                  Premium subscriptions are available to registered users with many more <a
                    href="/info/subscription_info"
                    target="_blank"
                  >features</a>
                </li>
              </ul>
            </div>

            <v-form
              ref="signUpForm"
              v-model="validSignUp"
              lazy-validation
              @submit.prevent="signUp()"
            >
              <v-text-field
                v-model.trim="signUpFields.email"
                :rules="[rules.required, rules.email]"
                autocorrect="off"
                spellcheck="false"
                prepend-icon="email"
                label="E-mail*"
                @keyup.enter="signUp()"
              />
              <v-text-field
                v-model.trim="signUpFields.password"
                :rules="[rules.required, rules.password]"
                :type="signUpFields.showPassword ? 'text' : 'password'"
                :append-icon="signUpFields.showPassword ? 'visibility_off' : 'visibility'"
                label="Password*"
                prepend-icon="lock"
                @click:append="signUpFields.showPassword = !signUpFields.showPassword"
                @keyup.enter="signUp()"
              />
              <v-text-field
                v-model.trim="signUpFields.passwordConfirm"
                :rules="[rules.required, rules.password, rules.confirmMatch]"
                :type="signUpFields.showPassword ? 'text' : 'password'"
                :append-icon="signUpFields.showPassword ? 'visibility_off' : 'visibility'"
                label="Confirm password*"
                prepend-icon="lock_outline"
                @click:append="signUpFields.showPassword = !signUpFields.showPassword"
                @keyup.enter="signUp()"
              />
              <v-checkbox v-model="signUpFields.quarterly" dense class="ma-0 pa-0">
              <template v-slot:label>
                      <div>Get Free
                      <a target="_blank" href="http://cdn.whalewisdom.com/newsletter/WhaleWisdom-2020-Q1.pdf"> Quarterly 13F Analysis</a>
                      </div>
                    </template>
              </v-checkbox>
              <v-checkbox v-model="signUpFields.weekly" dense class="ma-0 pa-0"
                            label="Get the Free Weekly Newsletter (coming soon)">
                            </v-checkbox>
            </v-form>
            <v-alert
              :value="Boolean(signUpFields.error)"
              type="error"
              class="mt-4"
            >
              {{ signUpFields.error }}
            </v-alert>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <!-- Button -->
      <v-card-actions class="pa-0">
        <v-btn
          v-if="currentTab === 'log-in-tab'"
          :loading="loading"
          :disabled="loading || !validLogIn"
          large
          block
          color="primary"
          class="ma-0"
          style="border-radius: 0; height: 56px"
          @click="logIn()"
        >
          Log in
          <v-icon>keyboard_arrow_right</v-icon>
        </v-btn>

        <v-btn
          v-if="currentTab === 'sign-up-tab' && !signUpCompleted"
          :loading="loading"
          :disabled="loading || !validSignUp"
          large
          block
          color="primary"
          class="ma-0"
          style="border-radius: 0; height: 56px"
          @click="signUp()"
        >
          Sign Up
          <v-icon>keyboard_arrow_right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Modules
import { apiRoot } from '@/store/api'
import { mapFields } from 'vuex-map-fields'

export default {
  data () {
    return {
      currentTab: 'log-in-tab',
      signUpCompleted: false,
      loading: false,
      validLogIn: true,
      validSignUp: true,
      logInFields: {
        email: null,
        password: null,
        rememberMe: false,
        showPassword: false,
        error: null
      },
      signUpFields: {
        email: null,
        password: null,
        passwordConfirm: null,
        showPassword: false,
        error: null,
        quarterly:true,
        weekly:true
      },
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail'
        },
        password: value => (value && value.length >= 4 && value.length <= 25) || 'Password must be 4-25 characters long',
        confirmMatch: () => this.signUpFields.password === this.signUpFields.passwordConfirm || 'Passwords do not match'
      }
    }
  },

  computed: {
    ...mapFields({
      dialog: 'app.loginDialog'
    })
  },

  watch: {
    dialog (isVisible) {
      if (isVisible) {
        this.signUpCompleted = false
        this.currentTab === 'log-in-tab' && this.$nextTick(() => this.$refs.email.focus())
      } else {
        this.$refs.logInForm.reset()
        this.$refs.signUpForm.reset()
      }
    }
  },

  methods: {
    logIn () {
      if (!this.$refs.logInForm.validate()) return

      this.loading = true
      this.logInFields.error = null

      apiRoot.get('sessions/dashboard_login', {
        params: {
          login: this.logInFields.email,
          password: this.logInFields.password,
          remember_me: this.logInFields.rememberMe
        }
      })
        .then(response => {
          this.$store.dispatch('user/loadUser')
          this.$store.dispatch('user/loadGroups')
          this.$store.dispatch('user/loadEmailAlerts')
          this.$store.dispatch('user/loadTrackedBacktests')
          this.$store.dispatch('user/loadInsiderAlerts')
          this.dialog = false
        })
        .catch(error => {
          this.logInFields.error = (error.response && error.response.data) || error.message || 'Log in failed. Check your login name and password.'
        })
        .then(() => (this.loading = false))
    },

    signUp () {
      if (!this.$refs.signUpForm.validate()) return

      this.loading = true
      this.signUpFields.error = null

      apiRoot.post('users/create_ajax', {
        user: {
          email: this.signUpFields.email,
          password: this.signUpFields.password,
          password_confirmation: this.signUpFields.passwordConfirm
        },
        weekly:this.signUpFields.weekly,
        quarterly:this.signUpFields.quarterly
      })
        .then(response => {
          this.signUpCompleted = true
        })
        .catch(error => {
          this.signUpFields.error = (error.response && error.response.data) || error.message || 'Sign up failed. Verify that your e-mail is correct and your password is between 4-25 characters.'
        })
        .then(() => (this.loading = false))
    }
  }
}
</script>
